/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  PageHeader,
  PageHeaderFooter,
  PageHeaderFooterLeft,
} from '@/components/shared/PageHeader';
import PageTabs from '@/components/shared/PageTabs';
import { TimelineFeed } from '@/components/shared/TimelineFeed';
import { TimelineItem } from '@/components/shared/TimelineFeed/TimelineItem';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useTranslator } from '@/hooks/useTranslator';
import { AssessmentAllMitigationsSheet } from '../../../../../../-components/AssessmentAllMitigationsSheet';
import { AssessmentRiskFactorsTable } from '../../../../../../-components/AssessmentRiskFactorsTable';
import { AssessmentRiskPrioritizationTab } from '../../../../../../-components/AssessmentRiskPrioritizationTab';
import { FulfilledRulesSheet } from '../../../../../../-components/FulfilledRulesSheet';
import { ManualMitigationDialog } from '../../../../../../-components/ManualMitigationDialog';
import { RemarksSheet } from '../../../../../../-components/RemarksSheet';
import { useAssessmentHistory } from '../../../../../../-hooks/useAssessmentHistory';
import { useAssessmentRiskFactors } from '../../../../../../-hooks/useAssessmentRiskFactors';
import { useCompanyAssessment } from '../../../../../../-hooks/useCompanyAssessment';
import { useCompanyAssessmentPrioritization } from '../../../../../../-hooks/useCompanyAssessmentPrioritization';
import { formatScoreFormula } from '../../../../../../-utils/formatScoreFormula';

function formatTriggerEvent(
  event: string | null,
  t: (k: string) => string,
): string {
  const key = `history.trigger.${event ?? 'INITIAL_CALCULATION'}`;
  const translated = t(key);
  // i18next returns the key when translation is missing — use formatted fallback
  if (translated === key) {
    const raw = event ?? 'INITIAL_CALCULATION';
    return raw
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }
  return translated;
}

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(detail)/assessments/$id/assessed/$assessmentId/',
)({
  validateSearch: z.object({ tab: z.string().optional() }),
  component: CompanyAssessmentDetailPage,
});

function CompanyAssessmentDetailPage() {
  const { id: blueprintId, assessmentId } = Route.useParams();
  const { tab } = Route.useSearch() as { tab?: string };
  const { getTranslation: t } = useTranslator({ page: 'riskAssessments' });

  const [remarksRisk, setRemarksRisk] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [allMitigationsOpen, setAllMitigationsOpen] = useState(false);
  const [fulfilledRulesTarget, setFulfilledRulesTarget] = useState<{
    triggerEvent: string;
    assets: string[];
  } | null>(null);
  const [mitigationTarget, setMitigationTarget] = useState<{
    risk: { id: string; title: string };
    metricId: 'LIKELIHOOD' | 'SEVERITY';
    currentScore: number | null;
  } | null>(null);

  const assessment = useCompanyAssessment(assessmentId);
  const riskFactors = useAssessmentRiskFactors(assessmentId);
  const history = useAssessmentHistory(assessmentId);
  const prioritization = useCompanyAssessmentPrioritization(assessmentId);

  const activeTab = tab ?? 'assessment';
  const d = assessment.data;

  if (assessment.isLoading) {
    return (
      <PageContainer className='gap-4'>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className='h-32 w-full' />
        ))}
      </PageContainer>
    );
  }

  const companyName = d?.companyData?.display_name?.value ?? '—';
  const apv = d?.companyData?.annual_purchase_volume;
  const notSet = t('companyDetail.notSet');
  const apvDisplay = apv?.value
    ? `${apv.value}${apv.unit ? ` ${apv.unit}` : ''}${apv.year ? ` (${apv.year})` : ''}`
    : notSet;
  const revenueDisplay = d?.companyData?.annual_revenue?.value ?? notSet;

  return (
    <div className='flex flex-col h-full overflow-hidden'>
      <PageHeader>
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center gap-2 flex-wrap'>
            <h1 className='text-lg font-semibold text-foreground'>
              {t('companyDetail.companyName')}: {companyName}
            </h1>
            {d?.isBlueprintArchived && (
              <span className='inline-flex items-center h-5 px-2 rounded-md bg-destructive/10 text-destructive text-xs font-semibold border border-destructive/20'>
                {t('companyDetail.archived')}
              </span>
            )}
          </div>
          <Link
            to='/risk/assessments/$id'
            params={{ id: blueprintId }}
            className='text-sm font-semibold text-foreground hover:underline w-fit'
          >
            {d?.blueprintName ?? '—'}
          </Link>
        </div>

        <PageHeaderFooter>
          <PageHeaderFooterLeft>
            <span>
              <strong>{t('companyDetail.annualPurchaseVolume')}:</strong>{' '}
              {apvDisplay}
            </span>
            <span>
              <strong>{t('companyDetail.annualRevenue')}:</strong>{' '}
              {revenueDisplay}
            </span>
            {d?.scoreFormula && (
              <span>
                <strong>{t('companyDetail.scoreFormula')}:</strong>{' '}
                <span className='font-mono'>
                  {formatScoreFormula(d.scoreFormula)}
                </span>
              </span>
            )}
          </PageHeaderFooterLeft>
        </PageHeaderFooter>
      </PageHeader>

      <PageTabs
        tabs={[
          { key: 'assessment', label: t('detail.tabs.assessment') },
          { key: 'prioritization', label: t('detail.tabs.prioritization') },
          { key: 'history', label: t('detail.tabs.history') },
        ]}
        defaultTab='assessment'
        param='tab'
        moreLabel={t('detail.tabs.more')}
      />

      <div className='flex-1 min-h-0 overflow-hidden'>
        {activeTab === 'assessment' && (
          <PageContainer className='h-full overflow-hidden'>
            <AssessmentRiskFactorsTable
              rows={riskFactors.rows}
              total={riskFactors.total}
              isLoading={riskFactors.isLoading}
              sorting={riskFactors.sorting}
              filters={riskFactors.filters}
              pagination={riskFactors.pagination}
              onSortingChange={riskFactors.onSortingChange}
              onFiltersChange={riskFactors.onFiltersChange}
              onPaginationChange={riskFactors.onPaginationChange}
              onRemarks={risk => setRemarksRisk(risk)}
              onEditMetric={(risk, metricId, currentScore) =>
                setMitigationTarget({ risk, metricId, currentScore })
              }
            />
          </PageContainer>
        )}

        {activeTab === 'prioritization' && (
          <PageContainer className='h-full overflow-y-auto'>
            <AssessmentRiskPrioritizationTab
              items={prioritization.data?.data ?? []}
              isLoading={prioritization.isLoading}
            />
          </PageContainer>
        )}

        {activeTab === 'history' && (
          <PageContainer className='h-full overflow-y-auto'>
            <TimelineFeed
              events={history.data?.pages.flatMap(p => p.data) ?? []}
              getEventId={e => e.id}
              getEventDate={e => e.createdAt}
              isLoading={history.isLoading}
              hasNextPage={history.hasNextPage}
              isFetchingNextPage={history.isFetchingNextPage}
              onLoadMore={() => void history.fetchNextPage()}
              renderEvent={event => (
                <TimelineItem
                  key={event.id}
                  createdAt={event.createdAt}
                  content={
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                      <StatusBadge
                        label={event.status}
                        variant={
                          event.status === 'READY'
                            ? 'success'
                            : event.status === 'CALCULATING'
                              ? 'warning'
                              : 'destructive'
                        }
                      />
                      {event.likelihoodScore != null && (
                        <span className='text-xs text-muted-foreground'>
                          {t('history.columns.likelihood')}:{' '}
                          <strong className='text-foreground'>
                            {event.likelihoodScore.toFixed(2)}
                          </strong>
                        </span>
                      )}
                      {event.severityScore != null && (
                        <span className='text-xs text-muted-foreground'>
                          {t('history.columns.severity')}:{' '}
                          <strong className='text-foreground'>
                            {event.severityScore.toFixed(2)}
                          </strong>
                        </span>
                      )}
                      {event.averagePriorityScore != null && (
                        <span className='text-xs text-muted-foreground'>
                          {t('history.columns.score')}:{' '}
                          <strong className='text-foreground'>
                            {event.averagePriorityScore.toFixed(2)}
                          </strong>
                        </span>
                      )}
                      <span className='text-xs text-muted-foreground'>
                        {formatTriggerEvent(
                          event.assessmentTrigger?.event ?? null,
                          t,
                        )}
                      </span>
                      {(event.assessmentTrigger?.assetName?.length ?? 0) >
                        0 && (
                        <button
                          type='button'
                          onClick={() => {
                            const trigger = event.assessmentTrigger;
                            if (!trigger?.event) return;
                            const assets = trigger.assetName?.length
                              ? trigger.assetName
                              : trigger.asset
                                ? [
                                    String(
                                      (trigger.asset as { id?: string }).id ??
                                        '',
                                    ),
                                  ]
                                : [];
                            setFulfilledRulesTarget({
                              triggerEvent: trigger.event,
                              assets,
                            });
                          }}
                          className='text-xs text-primary underline underline-offset-2 hover:no-underline'
                        >
                          {event.assessmentTrigger!.assetName!.join(', ')}
                        </button>
                      )}
                      {event.hasMitigation && (
                        <button
                          type='button'
                          onClick={() => setAllMitigationsOpen(true)}
                          className='text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors'
                        >
                          {t('history.viewMitigations')}
                        </button>
                      )}
                    </div>
                  }
                />
              )}
            />
          </PageContainer>
        )}
      </div>

      <RemarksSheet
        open={!!remarksRisk}
        onClose={() => setRemarksRisk(null)}
        assessmentId={assessmentId}
        riskId={remarksRisk?.id ?? ''}
        riskTitle={remarksRisk?.title ?? ''}
      />

      <ManualMitigationDialog
        open={!!mitigationTarget}
        onClose={() => setMitigationTarget(null)}
        assessmentId={assessmentId}
        companyId={d?.companyId ?? ''}
        riskId={mitigationTarget?.risk.id ?? ''}
        metricId={mitigationTarget?.metricId ?? 'LIKELIHOOD'}
      />

      <FulfilledRulesSheet
        open={!!fulfilledRulesTarget}
        onClose={() => setFulfilledRulesTarget(null)}
        assessmentId={assessmentId}
        triggerEvent={fulfilledRulesTarget?.triggerEvent ?? null}
        assets={fulfilledRulesTarget?.assets ?? []}
      />

      <AssessmentAllMitigationsSheet
        open={allMitigationsOpen}
        onClose={() => setAllMitigationsOpen(false)}
        assessmentId={assessmentId}
      />
    </div>
  );
}
